import * as React from 'react';

// API
import apibridge from '../../../apibridge';

// UTILS
import { validationErrorsToString } from '../../../utils';

// PACKAGES
import { Form, Formik, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import Button from '../../../components/Button';

// ICONS
import LoadingSpinner from '../../../components/LoadingSpinner';

// TYPES
import { closeModalType } from '../../../components/GlobalModal';

interface AccountPhoneModalTypes {
	accountDetails: {
		childAwareId?: string;
		email?: string;
		phoneNumber?: string;
	};
	closeModal?: closeModalType;
	getMyAccountData: () => Promise<void>;
}

const AccountPhoneModal: React.FC<AccountPhoneModalTypes> = ({ accountDetails, closeModal, getMyAccountData }) => {
	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(false);

	// INITIAL FORM VALUES
	const initialValues = {
		childawareId: accountDetails.childAwareId || undefined,
		phoneNumber: accountDetails.phoneNumber || undefined
	};

	// FORM VALIDATION FOR AUSTRALIAN PHONE NUMBERS
	const phoneRegExp =
		/^(?:\+?(61))? ?(?:\((?=.*\)))?(0?[2-57-8])\)? ?(\d\d(?:[- ](?=\d{3})|(?!\d\d[- ]?\d[- ]))\d\d[- ]?\d[- ]?\d{3})$/;

	// VALIDATION
	const validationSchema = Yup.object({
		phoneNumber: Yup.string().matches(phoneRegExp, 'Phone number is not valid').required('Required')
	});

	// SUBMIT
	const submitFormData = async (values: any) => {
		setIsLoading(true);

		const response = await apibridge.postAccountEditPhoneNumber({
			phoneNumber: values.phoneNumber
		});

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			getMyAccountData();

			if (closeModal) {
				closeModal();
			}
		}
	};

	return (
		<div className="email-modal">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<Formik validationSchema={validationSchema} initialValues={initialValues} onSubmit={submitFormData}>
								{(formik) => {
									return (
										<Form translate="yes">
											<div className="form-group">
												<label htmlFor="childawareId">ChildAware ID</label>

												<Field name="childawareId" type="text" readOnly className="form-control disabled-input" />
											</div>

											<div className="form-group">
												<label htmlFor="phoneNumber">Edit Phone Number</label>

												<Field name="phoneNumber" type="text" className="form-control" />
												<p className="text-subtitle">For a landline, please include an area code e.g. 03 XXX</p>

												<ErrorMessage component={'div'} className="error-message" name="phoneNumber" />
											</div>

											<Button disabled={isLoading} type="submit" className="btn-primary full-width">
												Continue
											</Button>
										</Form>
									);
								}}
							</Formik>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default AccountPhoneModal;
